import { useState, useMemo, useCallback, type MouseEvent } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  queryClient,
  adminBookingGetAllow404,
  apiRequest,
  fetchAdminBookingListJson,
  ADMIN_BOOKINGS_MAX_PAGE_SIZE,
} from "@/lib/queryClient";
import { useAdminServicesCatalog, useAdminStaffCatalog } from "@/hooks/useAdminPagedCatalogs";
import { useToast } from "@/hooks/useToast";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";
import { Add, ChevronLeft, ChevronRight, Close, Delete, LockOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import BookingDetailModal from "@/components/BookingDetailModal";
import {
  normalizeBookingsList,
  bookingToEditPreset,
  resolveOfferingName,
} from "@/lib/adminApi";
import { unwrapPagedList, coerceBookingApiEntityId } from "@/lib/bookingApi";
import { dialogActionsSx, dialogContentOutlinedFormSx, dialogPaperPropsLabelSafe } from "@/lib/dialogStyles";
import type { BookingWithDetails } from "@shared/schema";
import NewBookingDrawer from "@/components/NewBookingDrawer";
import {
  assignOverlapLanes,
  CALENDAR_EVENT_INSET_PX,
  CALENDAR_LANE_GAP_PX,
} from "@/lib/calendarOverlapLayout";
import {
  calendarBookingEventBaseSx,
  calendarBookingSubtitleSx,
  calendarBookingTitleSx,
  staffPaletteKeyFromIndex,
} from "@/lib/calendarEventCardStyles";
import { sameEntityId } from "@/lib/utils";

/** Matches booking API blockout payload (proxied `/api/admin/blockouts`). `staffId` is a JSON number or null. */
export interface CalendarBlockout {
  id: string;
  businessId?: string;
  staffId: number | null;
  startsAt: string;
  endsAt: string;
  reason: string | null;
  createdAt?: string;
}

function blockoutStaffIdFromApi(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Resolves blockout POST `staffId`: `null` for whole team, or a **safe integer** local staff id.
 * UUID / opaque ids must not become `null` silently (that would look like an all-staff blockout).
 */
type BlockoutStaffIdForApiResult =
  | { ok: true; staffId: number | null }
  | { ok: false; reason: "missing" | "not_safe_integer" };

function resolveBlockoutStaffIdForApi(staffScope: "all" | "one", staffId: string): BlockoutStaffIdForApiResult {
  if (staffScope === "all") {
    return { ok: true as const, staffId: null };
  }
  const raw = staffId.trim();
  if (!raw) {
    return { ok: false as const, reason: "missing" };
  }
  const coerced = coerceBookingApiEntityId(raw);
  if (typeof coerced !== "number" || !Number.isSafeInteger(coerced)) {
    return { ok: false as const, reason: "not_safe_integer" };
  }
  return { ok: true as const, staffId: coerced };
}

const GRID_START_HOUR = 8;
const GRID_END_HOUR = 20;
const GRID_HOURS = GRID_END_HOUR - GRID_START_HOUR;

export default function AdminCalendar() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<"day" | "week">("week");
  const [staffFilter, setStaffFilter] = useState<string>("all");
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [newBookingPreset, setNewBookingPreset] = useState<
    { serviceId?: string; staffId?: string; customerId?: string; date?: string; time?: string } | undefined
  >(undefined);
  const [blockTimeOpen, setBlockTimeOpen] = useState(false);
  const [blockForm, setBlockForm] = useState({
    staffScope: "all" as "all" | "one",
    staffId: "",
    date: "",
    startTime: "09:00",
    endTime: "17:00",
    reason: "",
  });
  const [deleteBlockId, setDeleteBlockId] = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<BookingWithDetails | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: bookings, isLoading } = useQuery({
    queryKey: ["/api/admin/bookings", "list", 1, ADMIN_BOOKINGS_MAX_PAGE_SIZE],
    queryFn: () => fetchAdminBookingListJson(1, ADMIN_BOOKINGS_MAX_PAGE_SIZE),
    select: (data: unknown) => normalizeBookingsList(data) as BookingWithDetails[],
  });
  const { staff: staffList } = useAdminStaffCatalog();
  const { services: servicesCatalog } = useAdminServicesCatalog();

  const weekDays = useMemo(() => {
    const start = new Date(currentDate);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [currentDate]);

  const activeStaff = staffList?.filter((s) => s.active) ?? [];

  const calendarBookings = useMemo(() => bookings ?? [], [bookings]);

  const displayDays = useMemo(() => (view === "day" ? [new Date(currentDate)] : weekDays), [view, currentDate, weekDays]);

  const blockoutRange = useMemo(() => {
    const first = displayDays[0];
    const last = displayDays[displayDays.length - 1];
    const start = new Date(first);
    start.setHours(0, 0, 0, 0);
    const end = new Date(last);
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [displayDays]);

  const { data: blockouts = [], isLoading: loadingBlockouts } = useQuery<CalendarBlockout[]>({
    queryKey: ["/api/admin/blockouts", blockoutRange.start, blockoutRange.end],
    queryFn: async () => {
      const path = `/api/admin/blockouts?start=${encodeURIComponent(blockoutRange.start)}&end=${encodeURIComponent(blockoutRange.end)}`;
      const res = await adminBookingGetAllow404(path);
      if (!res) return [];
      const json = await res.json();
      return unwrapPagedList<CalendarBlockout>(json).map((bl) => ({
        ...bl,
        staffId: blockoutStaffIdFromApi(bl.staffId),
      }));
    },
  });

  const createBlockMutation = useMutation({
    mutationFn: async (body: { staffId: number | null; startsAt: string; endsAt: string; reason: string | null }) => {
      await apiRequest("POST", "/api/admin/blockouts", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blockouts"] });
      toast({ title: t("calendar.blockTimeCreated") });
      setBlockTimeOpen(false);
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/blockouts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blockouts"] });
      toast({ title: t("calendar.blockTimeRemoved") });
      setDeleteBlockId(null);
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  function navigate(direction: number) {
    const next = new Date(currentDate);
    if (view === "day") {
      next.setDate(next.getDate() + direction);
    } else {
      next.setDate(next.getDate() + direction * 7);
    }
    setCurrentDate(next);
  }

  function goToToday() {
    setCurrentDate(new Date());
  }

  const hours = Array.from({ length: 12 }, (_, i) => i + GRID_START_HOUR);

  const getBookingsForDay = useCallback(
    (date: Date) => {
      if (!calendarBookings.length) return [];
      return calendarBookings.filter((b) => {
        const bDate = new Date(b.startAt);
        if (
          bDate.getFullYear() !== date.getFullYear() ||
          bDate.getMonth() !== date.getMonth() ||
          bDate.getDate() !== date.getDate() ||
          b.status === "cancelled"
        ) {
          return false;
        }
        if (staffFilter !== "all") {
          const sid = b.staffId ?? null;
          if (!sid || !sameEntityId(sid, staffFilter)) return false;
        }
        return true;
      });
    },
    [calendarBookings, staffFilter],
  );

  const blockVisibleForFilter = useCallback(
    (bl: CalendarBlockout) => {
      if (staffFilter === "all") return true;
      if (bl.staffId == null) return true;
      return sameEntityId(bl.staffId, staffFilter);
    },
    [staffFilter],
  );

  const getBlockoutsForDay = useCallback(
    (date: Date) => blockouts.filter((bl) => {
        if (!blockVisibleForFilter(bl)) return false;
        const start = new Date(bl.startsAt);
        return (
          start.getFullYear() === date.getFullYear() && start.getMonth() === date.getMonth() && start.getDate() === date.getDate()
        );
      }),
    [blockouts, blockVisibleForFilter],
  );

  function getBlockSegmentStyle(bl: CalendarBlockout, day: Date): { top: string; height: string } | null {
    const rangeStart = new Date(bl.startsAt);
    const rangeEnd = new Date(bl.endsAt);
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);
    const segStart = rangeStart > dayStart ? rangeStart : dayStart;
    const segEnd = rangeEnd < dayEnd ? rangeEnd : dayEnd;
    if (segStart >= segEnd) return null;
    let hStart = segStart.getHours() + segStart.getMinutes() / 60;
    let hEnd = segEnd.getHours() + segEnd.getMinutes() / 60;
    hStart = Math.max(hStart, GRID_START_HOUR);
    hEnd = Math.min(hEnd, GRID_END_HOUR);
    if (hStart >= hEnd) return null;
    const top = ((hStart - GRID_START_HOUR) / GRID_HOURS) * 100;
    const height = ((hEnd - hStart) / GRID_HOURS) * 100;
    return { top: `${top}%`, height: `${Math.max(height, 2)}%` };
  }

  function getBookingPosition(booking: BookingWithDetails) {
    const start = new Date(booking.startAt);
    const end = new Date(booking.endAt);
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    const top = ((startHour - GRID_START_HOUR) / GRID_HOURS) * 100;
    const height = ((endHour - startHour) / GRID_HOURS) * 100;
    return { top: `${Math.max(0, top)}%`, height: `${Math.min(100 - top, height)}%` };
  }

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const headerText =
    view === "day"
      ? currentDate.toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : `${weekDays[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${weekDays[6].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;

  function openBlockDialog() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    setBlockForm({
      staffScope: "all",
      staffId: activeStaff[0]?.id ?? "",
      date: `${y}-${m}-${day}`,
      startTime: "09:00",
      endTime: "17:00",
      reason: "",
    });
    setBlockTimeOpen(true);
  }

  function openNewBookingAt(day: Date, hour: number, minute: number) {
    const hh = String(hour).padStart(2, "0");
    const mm = String(minute).padStart(2, "0");
    const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
    setNewBookingPreset({
      date: dateStr,
      time: `${hh}:${mm}`,
      ...(staffFilter !== "all" ? { staffId: staffFilter } : {}),
    });
    setNewBookingOpen(true);
  }

  /** Hour cell: minute from click Y (15‑min steps). */
  function openCalendarSlotHour(day: Date, hour: number, e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - rect.top) / rect.height;
    const minute = Math.min(45, Math.round((y * 60) / 15) * 15);
    openNewBookingAt(day, hour, minute);
  }

  function submitBlock() {
    const { staffScope, staffId, date, startTime, endTime, reason } = blockForm;
    if (!date || !startTime || !endTime) return;
    const startsAt = new Date(`${date}T${startTime.length === 5 ? `${startTime}:00` : startTime}`).toISOString();
    const endsAt = new Date(`${date}T${endTime.length === 5 ? `${endTime}:00` : endTime}`).toISOString();
    if (new Date(endsAt) <= new Date(startsAt)) {
      toast({ title: t("calendar.blockTimeEndAfterStart"), variant: "destructive" });
      return;
    }
    const staffResolution = resolveBlockoutStaffIdForApi(staffScope, staffId);
    if (staffResolution.ok === false) {
      if (staffResolution.reason === "missing") {
        toast({ title: t("calendar.blockTimeStaffRequired"), variant: "destructive" });
      } else {
        toast({ title: t("calendar.blockTimeStaffIdInvalid"), variant: "destructive" });
      }
      return;
    }
    createBlockMutation.mutate({
      staffId: staffResolution.staffId,
      startsAt,
      endsAt,
      reason: reason.trim() || null,
    });
  }

  const calendarLoading = isLoading || loadingBlockouts;

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", maxWidth: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" fontWeight="bold" data-testid="text-calendar-title">
            {t("calendar.title")}
          </Typography>
          <Button size="small" onClick={() => navigate(-1)} data-testid="button-calendar-prev">
            <ChevronLeft />
          </Button>
          <Button size="small" variant="outlined" onClick={goToToday} data-testid="button-calendar-today">
            {t("calendar.today")}
          </Button>
          <Button size="small" onClick={() => navigate(1)} data-testid="button-calendar-next">
            <ChevronRight />
          </Button>
          <Typography variant="body2" color="text.secondary">
            {headerText}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" startIcon={<LockOutlined />} onClick={openBlockDialog} data-testid="button-block-time">
            {t("calendar.blockTime")}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setNewBookingPreset(undefined);
              setNewBookingOpen(true);
            }}
            data-testid="button-calendar-new-booking"
          >
            {t("calendar.newBooking")}
          </Button>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="calendar-staff-label">{t("calendar.allStaff")}</InputLabel>
            <Select
              labelId="calendar-staff-label"
              label={t("calendar.allStaff")}
              value={staffFilter}
              onChange={(e) => setStaffFilter(e.target.value)}
              data-testid="select-calendar-staff"
            >
              <MenuItem value="all">{t("adminBookings.staffAll")}</MenuItem>
              {activeStaff.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="view-label">{t("calendar.view")}</InputLabel>
            <Select
              labelId="view-label"
              label={t("calendar.view")}
              value={view}
              onChange={(e) => setView(e.target.value as "day" | "week")}
              data-testid="select-calendar-view"
            >
              <MenuItem value="day">{t("calendar.day")}</MenuItem>
              <MenuItem value="week">{t("calendar.week")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {calendarLoading ? (
        <Skeleton variant="rounded" sx={{ flex: 1, minHeight: 400 }} />
      ) : (
        <Box sx={{ flex: 1, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
          <Box sx={{ minWidth: 600 }}>
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: `60px repeat(${displayDays.length}, 1fr)`,
                borderBottom: 1,
                borderColor: "divider",
                position: "sticky",
                top: 0,
                /** Above absolute booking/block layers (z-index 1–2) so day labels stay readable when scrolling. */
                zIndex: theme.zIndex.appBar,
                bgcolor: "background.paper",
                boxShadow: theme.shadows[1],
              })}
            >
              <Box sx={{ p: 1, borderRight: 1, borderColor: "divider" }} />
              {displayDays.map((day, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1,
                    textAlign: "center",
                    borderRight: i < displayDays.length - 1 ? 1 : 0,
                    borderColor: "divider",
                    bgcolor: isToday(day) ? "action.selected" : undefined,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {day.toLocaleDateString("en-GB", { weekday: "short" })}
                  </Typography>
                  <Typography variant="h6" color={isToday(day) ? "primary" : "text.primary"}>
                    {day.getDate()}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ position: "relative" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: `60px repeat(${displayDays.length}, 1fr)` }}>
                {hours.map((hour) => (
                  <Box key={hour} sx={{ display: "contents" }}>
                    <Box sx={{ height: 64, borderRight: 1, borderBottom: 1, borderColor: "divider", p: 0.5, textAlign: "right", fontSize: 12, color: "text.secondary" }}>
                      {hour % 12 || 12}
                      {hour >= 12 ? "p" : "a"}
                    </Box>
                    {displayDays.map((day, dayIdx) => (
                      <Box
                        key={dayIdx}
                        sx={{
                          height: 64,
                          borderRight: dayIdx < displayDays.length - 1 ? 1 : 0,
                          borderBottom: 1,
                          borderColor: "divider",
                          bgcolor: isToday(day) ? "action.hover" : undefined,
                        }}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  gridTemplateColumns: `60px repeat(${displayDays.length}, 1fr)`,
                  /** Must stay interactive: `pointer-events: none` on a parent blocks hits to children in several browsers. */
                  pointerEvents: "auto",
                }}
              >
                <Box sx={{ pointerEvents: "none" }} aria-hidden />
                {displayDays.map((day, dayIdx) => {
                  const dayBookings = getBookingsForDay(day);
                  const bookingLanes = assignOverlapLanes(dayBookings);
                  const dayBlocks = getBlockoutsForDay(day);
                  return (
                    <Box
                      key={dayIdx}
                      sx={{
                        position: "relative",
                        height: hours.length * 64,
                        pointerEvents: "auto",
                      }}
                      data-testid={`calendar-day-column-${dayIdx}`}
                    >
                      {hours.map((hour, hIdx) => (
                        <Box
                          key={hour}
                          role="button"
                          tabIndex={0}
                          aria-label={String(t("calendar.slotNewBooking", { time: `${hour}:00` }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openNewBookingAt(day, hour, 0);
                            }
                          }}
                          onClick={(e) => openCalendarSlotHour(day, hour, e)}
                          sx={(theme) => ({
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: hIdx * 64,
                            height: 64,
                            zIndex: 0,
                            pointerEvents: "auto",
                            cursor: "pointer",
                            boxSizing: "border-box",
                            transition: "background-color 120ms ease",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.08),
                            },
                            "&:hover .calendar-slot-add": {
                              opacity: 1,
                            },
                            "&:focus-visible": {
                              outline: `2px solid ${theme.palette.primary.main}`,
                              outlineOffset: -2,
                            },
                          })}
                          data-testid={`calendar-slot-${dayIdx}-${hour}`}
                        >
                          <Add
                            className="calendar-slot-add"
                            aria-hidden
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 6,
                              fontSize: 20,
                              opacity: 0,
                              pointerEvents: "none",
                              color: "text.secondary",
                              transition: "opacity 120ms ease",
                            }}
                          />
                        </Box>
                      ))}
                      {dayBlocks.map((bl) => {
                        const pos = getBlockSegmentStyle(bl, day);
                        if (!pos) return null;
                        const staffName = bl.staffId
                          ? activeStaff.find((s) => sameEntityId(s.id, bl.staffId))?.displayName
                          : null;
                        return (
                          <Box
                            key={bl.id}
                            sx={(theme) => ({
                              position: "absolute",
                              left: 4,
                              right: 4,
                              borderRadius: 1,
                              border: 1,
                              borderColor: theme.palette.warning.main,
                              borderStyle: "dashed",
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? alpha(theme.palette.warning.main, 0.28)
                                  : alpha(theme.palette.warning.main, 0.12),
                              px: 0.5,
                              py: 0.25,
                              overflow: "hidden",
                              pointerEvents: "auto",
                              top: pos.top,
                              height: pos.height,
                              minHeight: 20,
                              zIndex: 1,
                              color: theme.palette.mode === "dark" ? theme.palette.warning.light : theme.palette.warning.dark,
                            })}
                            data-testid={`event-block-${bl.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" fontWeight={700} noWrap display="block" color="inherit">
                                  {t("calendar.blockedLabel")}
                                </Typography>
                                <Typography variant="caption" noWrap display="block" sx={{ fontSize: 10, opacity: 0.88 }} color="inherit">
                                  {bl.reason || t("calendar.blockTimeNoReason")}
                                  {staffName ? ` · ${staffName}` : ""}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                aria-label={t("calendar.blockTimeRemove")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteBlockId(bl.id);
                                }}
                                data-testid={`button-remove-block-${bl.id}`}
                              >
                                <Close fontSize="inherit" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                      {dayBookings.map((booking) => {
                        const pos = getBookingPosition(booking);
                        const { lane, maxLanes } = bookingLanes.get(booking.id) ?? { lane: 0, maxLanes: 1 };
                        const inset = CALENDAR_EVENT_INSET_PX;
                        const gap = CALENDAR_LANE_GAP_PX;
                        const totalGaps = Math.max(0, maxLanes - 1) * gap;
                        const left =
                          maxLanes <= 1
                            ? inset
                            : `calc(${inset}px + ${lane} * ((100% - ${2 * inset}px - ${totalGaps}px) / ${maxLanes} + ${gap}px))`;
                        const width =
                          maxLanes <= 1
                            ? `calc(100% - ${2 * inset}px)`
                            : `calc((100% - ${2 * inset}px - ${totalGaps}px) / ${maxLanes})`;
                        const staffIdx = Math.max(
                          0,
                          staffList?.findIndex((s) => sameEntityId(s.id, booking.staffId)) ?? 0,
                        );
                        const paletteKey = staffPaletteKeyFromIndex(staffIdx);
                        const narrow = maxLanes > 1;
                        return (
                          <Box
                            key={booking.id}
                            sx={[
                              calendarBookingEventBaseSx(theme, paletteKey, narrow),
                              {
                                position: "absolute",
                                left,
                                width,
                                right: "auto",
                                cursor: "pointer",
                                pointerEvents: "auto",
                                top: pos.top,
                                height: pos.height,
                                minHeight: 20,
                                zIndex: 2,
                              },
                            ]}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking(booking);
                              setDetailOpen(true);
                            }}
                            data-testid={`event-booking-${booking.id}`}
                          >
                            <Typography
                              variant="caption"
                              display="-webkit-box"
                              sx={[
                                calendarBookingTitleSx(theme, narrow),
                                {
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  wordBreak: "break-word",
                                },
                              ]}
                            >
                              {booking.customer?.firstName} {booking.customer?.lastName}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="-webkit-box"
                              sx={[
                                calendarBookingSubtitleSx(theme, narrow),
                                {
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  wordBreak: "break-word",
                                },
                              ]}
                            >
                              {resolveOfferingName(booking, servicesCatalog) ?? ""}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <NewBookingDrawer
        open={newBookingOpen}
        onOpenChange={(o) => {
          setNewBookingOpen(o);
          if (!o) setNewBookingPreset(undefined);
        }}
        preset={newBookingPreset}
      />

      <Dialog
        open={blockTimeOpen}
        onClose={() => setBlockTimeOpen(false)}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        PaperProps={dialogPaperPropsLabelSafe}
      >
        <DialogTitle>{t("calendar.blockTimeTitle")}</DialogTitle>
        <DialogContent sx={dialogContentOutlinedFormSx}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="block-staff-scope" shrink>
              {t("calendar.blockTimeAppliesTo")}
            </InputLabel>
            <Select
              labelId="block-staff-scope"
              label={t("calendar.blockTimeAppliesTo")}
              value={blockForm.staffScope}
              notched
              onChange={(e) => {
                const v = e.target.value as "all" | "one";
                setBlockForm((f) => {
                  if (v === "one" && !f.staffId.trim() && activeStaff[0]) {
                    return { ...f, staffScope: v, staffId: activeStaff[0].id };
                  }
                  return { ...f, staffScope: v };
                });
              }}
              data-testid="select-block-staff-scope"
            >
              <MenuItem value="all">{t("calendar.blockTimeAllTeam")}</MenuItem>
              <MenuItem value="one">{t("calendar.blockTimeOneStaff")}</MenuItem>
            </Select>
          </FormControl>
          {blockForm.staffScope === "one" && (
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="block-staff-pick" shrink>
                {t("calendar.staff")}
              </InputLabel>
              <Select
                labelId="block-staff-pick"
                label={t("calendar.staff")}
                value={blockForm.staffId}
                notched
                onChange={(e) => setBlockForm((f) => ({ ...f, staffId: e.target.value }))}
                data-testid="select-block-staff"
              >
                {activeStaff.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            type="date"
            label={t("calendar.blockTimeDate")}
            value={blockForm.date}
            onChange={(e) => setBlockForm((f) => ({ ...f, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            data-testid="input-block-date"
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              type="time"
              label={t("calendar.blockTimeStart")}
              value={blockForm.startTime}
              onChange={(e) => setBlockForm((f) => ({ ...f, startTime: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              data-testid="input-block-start"
            />
            <TextField
              type="time"
              label={t("calendar.blockTimeEnd")}
              value={blockForm.endTime}
              onChange={(e) => setBlockForm((f) => ({ ...f, endTime: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              data-testid="input-block-end"
            />
          </Box>
          <TextField
            label={t("calendar.blockTimeReason")}
            value={blockForm.reason}
            onChange={(e) => setBlockForm((f) => ({ ...f, reason: e.target.value }))}
            fullWidth
            size="small"
            multiline
            minRows={2}
            data-testid="input-block-reason"
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setBlockTimeOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={submitBlock}
            disabled={createBlockMutation.isPending || !blockForm.date}
            data-testid="button-save-block"
          >
            {createBlockMutation.isPending ? t("common.saving") : t("calendar.blockTimeSave")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteBlockId)} onClose={() => setDeleteBlockId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("calendar.blockTimeRemoveTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("calendar.blockTimeRemoveBody")}
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setDeleteBlockId(null)}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<Delete />}
            disabled={deleteBlockMutation.isPending}
            onClick={() => deleteBlockId && deleteBlockMutation.mutate(deleteBlockId)}
            data-testid="button-confirm-remove-block"
          >
            {t("calendar.blockTimeRemove")}
          </Button>
        </DialogActions>
      </Dialog>

      <BookingDetailModal
        booking={selectedBooking}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        staffList={staffList}
        onEdit={(b) => {
          setNewBookingPreset(bookingToEditPreset(b));
          setDetailOpen(false);
          setNewBookingOpen(true);
        }}
      />
    </Box>
  );
}
